import { EFinancingProgram } from '@if/core/types'
import { formatCurrencyNoDecimals, formatUtcIsoDateTime } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { useUrlFiltersAndPagination } from '@if/mui'
import { Button, Chip, Link, Stack, Toolbar } from '@mui/material'
import { DataGrid, GridColDef, GridFooter, GridFooterContainer, GridRenderCellParams } from '@mui/x-data-grid'
import { FilterPreferenceActionBar } from '@src/components/FilterPreferenceActionBar'
import {
  CREDIT_TASK_DASHBOARD_FILTERS_ENABLED_KEY,
  CREDIT_TASK_DASHBOARD_FILTERS_KEY,
} from '@src/contexts/preferences-context'
import {
  useGetCreditTaskDashboardEntries,
  useGetStatusCount,
  useGetTrusteeNames,
  useReserveCreditTask,
} from '@src/data/api/credit-task-dashboard-entry/credit-task-DashboardEntries-api'
import { useFeatureFlags, useMerchantTags } from '@src/data/selectors/app-config-selectors'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { omit } from 'lodash-es'
import { MouseEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FullScreenContainer from '../../components/FullScreenContainer'
import {
  CreditApplicationReservation,
  CreditApplicationReservationDto,
  CreditTaskDashboardEntry,
  CreditTaskDashboardFilters,
  EEntryType
} from '../../data/types'
import CountStatuses from './components/CountStatuses'
import FilterComponent from './components/filters'
import FollowUpAgentCell from './components/FollowUpAgentCell'
import CustomToolBar from './components/toolBar'

// Schema for the filter values stored in the URL. Pagination and `financingCompanyId` are
// intentionally not part of the schema — pagination is owned by `useUrlFiltersAndPagination`,
// and `financingCompanyId` comes from the global PreferencesProvider context.
const filterSchema = yup.object({
  financingProgramIds: yup.array(yup.string().required()).default([]),
  entryType: yup.array(yup.string<EEntryType>().required()).default([]),
  correspondanceLanguageId: yup.array(yup.number().required()).default([]),
  assignation: yup.string().default('blank'),
  trusteeName: yup.string().default(''),
  statesIso: yup.array(yup.string().required()).default([]),
  dateRange: yup.string().default(''),
  minGrantedAmount: yup.string().default(''),
  maxGrantedAmount: yup.string().default(''),
  sortByGrantedAmountDesc: yup.boolean().default(false),
})

const CreditTaskDashboardPage = () => {
  const { t } = useTranslation()
  const { selectedCompany } = usePreferences()
  const merchantTags = useMerchantTags()
  const featureFlags = useFeatureFlags()

  const [openSearchFilters, setOpenSearchFilters] = useState<boolean>(false)

  const { filters, setFilters, reset, preferences, pagination, setPagination } =
    useUrlFiltersAndPagination(filterSchema, {
      storageKey: CREDIT_TASK_DASHBOARD_FILTERS_KEY,
      preferencesEnabledKey: CREDIT_TASK_DASHBOARD_FILTERS_ENABLED_KEY,
      defaultLimit: 50,
    })

  // Children expect a fully-populated `CreditTaskDashboardFilters` (including pagination and
  // `financingCompanyId`). Compose it for them — they don't need to know our internal split.
  const filterValues = useMemo<CreditTaskDashboardFilters>(
    () => ({
      ...filters,
      financingCompanyId: selectedCompany ?? '',
      limit: pagination.limit,
      offset: pagination.offset,
    }),
    [filters, selectedCompany, pagination],
  )

  const queryParams = useMemo(() => {
    const dates = filterValues.dateRange ? filterValues.dateRange.split('@') : ['', '']
    const endDate = dates[1] || formatUtcIsoDateTime(new Date())
    const startDate = dates[0]
    return {
      ...omit(filterValues, ['dateRange', 'minGrantedAmount', 'maxGrantedAmount', 'sortByGrantedAmountDesc']),
      startDate,
      endDate,
      ...(filterValues.minGrantedAmount ? { minGrantedAmount: filterValues.minGrantedAmount } : {}),
      ...(filterValues.maxGrantedAmount ? { maxGrantedAmount: filterValues.maxGrantedAmount } : {}),
      ...(filterValues.sortByGrantedAmountDesc ? { sortByGrantedAmountDesc: 'true' } : {}),
    }
  }, [filterValues])

  const [taskList, isLoading] = useGetCreditTaskDashboardEntries(queryParams)
  const [statusCount] = useGetStatusCount(queryParams)
  const [trusteeNames] = useGetTrusteeNames()
  const [reserveCreditTask] = useReserveCreditTask(queryParams)
  const user = useUser()
  const trusteeNamesWithEmptyOption = useMemo(() => ['', ...trusteeNames], [trusteeNames])
  const userCanEditCreditApplication = user?.rights.canManageDocuments || user?.rights.canManageTasks

  const handleApplyFilter = useCallback(
    (data: CreditTaskDashboardFilters) => {
      // Pagination and `financingCompanyId` are not schema fields — strip them. `setFilters`
      // auto-resets offset to 0 (page 1) so the user doesn't land on an empty page.
      const { limit: _l, offset: _o, financingCompanyId: _f, ...filterPatch } = data
      setFilters(filterPatch)
    },
    [setFilters],
  )

  const handleReserve = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      const values = e.currentTarget.value.split(',')
      const entityLockToAdd: CreditApplicationReservationDto = {
        financingProgramId: values[1] as EFinancingProgram,
        creditApplicationId: values[0],
        durationInMinutes: 20,
      }
      try {
        const data: CreditApplicationReservation = await reserveCreditTask(entityLockToAdd)
        if (data.isAcquired) window.open(`/Applications/${values[1]}/${data.creditApplicationId}/view`, '_blank')
      } catch (error) {
        reportErrorToConsole(error)
      }
    },
    [reserveCreditTask],
  )

  const handleBadgeClick = (entryType: EEntryType[]) => {
    setFilters({ entryType })
  }

  const columns = useMemo<GridColDef<CreditTaskDashboardEntry>[]>(
    () => [
      {
        field: 'creditApplicationReferenceNumber',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.applicationNumber'),
        minWidth: 130,
        sortable: false,
        renderCell: (params) => {
          return (
            <div>
              <Link href={`/Applications/${params.row.financingProgramId}/${params.row.id}/view`}>
                {params.row.creditApplicationReferenceNumber}
              </Link>
              {params.row.isRush && <span style={{ color: 'red', fontWeight: 'bold' }}> ( ! )</span>}
            </div>
          )
        },
      },
      {
        field: 'reservedByUserId',
        headerName: t('taskManager.reservedBy'),
        minWidth: 130,
        sortable: false,
        renderCell: (params) => {
          if (params.row.reservedByUserId === null) {
            return (
              <Button
                variant="outlined"
                value={[params.row.id, params.row.financingProgramId]}
                onClick={handleReserve}
                disabled={!userCanEditCreditApplication}
              >
                {t('taskManager.reserve')}
              </Button>
            )
          }
          if (user?.id === params.row.reservedByUserId) {
            return t('taskManager.yourself')
          }
          return params.row.reservedByUserName
        },
      },
      { field: 'merchantName', headerName: t('taskManager.merchantName'), flex: 1, sortable: false },
      {
        field: 'financingProgramId',
        headerName: t('taskManager.financingProgram'),
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          return t(`EFinancingProgram.${params.row.financingProgramId}`, { ns: 'enum' })
        },
      },
      { field: 'clientName', headerName: t('taskManager.clientName'), flex: 1, sortable: false },
      { field: 'stateIso', headerName: 'Province', minWidth: 100, sortable: false },
      ...(featureFlags.useTaskDashboardGrantedAmount
        ? [
            {
              field: 'maxAmountFinanced',
              headerName: t('browseCreditApplications.dataGridColumnHeaders.grantedAmount'),
              minWidth: 140,
              align: 'right' as const,
              headerAlign: 'right' as const,
              sortable: false,
              renderCell: (params: GridRenderCellParams<CreditTaskDashboardEntry>) => {
                return formatCurrencyNoDecimals(params.row.maxAmountFinanced)
              },
            },
          ]
        : []),
      {
        field: 'tags',
        headerName: t('taskManager.tags'),
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack gap={0.5} direction="row" flexWrap="wrap">
            {params.row.tags?.map((tag) => (
              <Chip key={tag} size="small" label={merchantTags.find((mt) => mt.id === tag)?.text} />
            ))}
          </Stack>
        ),
      },
      ...(featureFlags.useFollowUpAgentId
        ? [
            {
              field: 'followUpAgent',
              headerName: t('followUpAgent.label'),
              flex: 1,
              sortable: false,
              renderCell: (params: GridRenderCellParams<CreditTaskDashboardEntry>) => {
                const agent = (params.row as unknown as Record<string, unknown>).followUpAgent as
                  | { id: string; firstName: string; lastName: string; initials: string }
                  | undefined
                  | null
                return (
                  <FollowUpAgentCell
                    followUpAgent={agent}
                    creditApplicationId={params.row.id}
                    financingProgramId={params.row.financingProgramId}
                    currentFilters={queryParams}
                  />
                )
              },
            },
          ]
        : []),
      {
        field: 'followUpReason',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.followUpReason'),
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          return t(`taskManager.${params.row.actions[0]?.actionType}`)
        },
      },
    ],
    [
      t,
      user?.id,
      handleReserve,
      userCanEditCreditApplication,
      merchantTags,
      featureFlags.useFollowUpAgentId,
      featureFlags.useTaskDashboardGrantedAmount,
      queryParams,
    ],
  )

  const handleOnPageChange = useCallback(
    (direction: number) => {
      const delta = direction === 0 ? -pagination.limit : pagination.limit
      setPagination({ offset: pagination.offset + delta, limit: pagination.limit })
    },
    [pagination, setPagination],
  )

  const customToolBar = () => <CustomToolBar totalItems={taskList.length} />
  const customFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooter sx={{ '& .MuiTablePagination-actions': { display: 'none' } }} />
        <div style={{ display: 'flex' }}>
          <Button onClick={() => handleOnPageChange(0)} disabled={pagination.offset === 0}>
            {' '}
            {t('common.previous')}
          </Button>
          <Button onClick={() => handleOnPageChange(1)} disabled={taskList.length < pagination.limit}>
            {' '}
            {t('common.next')}
          </Button>
        </div>
      </GridFooterContainer>
    )
  }
  const onCancel = useCallback(() => {
    setOpenSearchFilters(false)
  }, [setOpenSearchFilters])

  const paginationModel = useMemo(
    () => ({ pageSize: pagination.limit, page: Math.floor(pagination.offset / pagination.limit) }),
    [pagination.limit, pagination.offset],
  )

  return (
    <FullScreenContainer>
      <FilterComponent
        filterValues={filterValues}
        resetFilter={reset}
        applyFilter={handleApplyFilter}
        trusteeNames={trusteeNamesWithEmptyOption}
        open={openSearchFilters}
        onCancel={onCancel}
      />

      <Toolbar sx={{justifyContent: 'space-between'}}>
        <CountStatuses
          statusCount={statusCount}
          setOpenSearchFilters={setOpenSearchFilters}
          resetFilters={reset}
          onBadgeClick={handleBadgeClick}
        />
        <FilterPreferenceActionBar
          preferences={preferences}
          preferenceKey={CREDIT_TASK_DASHBOARD_FILTERS_ENABLED_KEY}
        />
      </Toolbar>
      <div style={{ height: '36rem' }}>
        {taskList.length === 0 && !isLoading ? <h3> {t('common.noResultFound')} </h3> : ''}
        <DataGrid
          rows={taskList}
          columns={columns}
          checkboxSelection
          disableColumnFilter
          disableColumnMenu
          paginationModel={paginationModel}
          onPaginationModelChange={({ pageSize }) =>
            setPagination({ limit: pageSize, offset: pagination.offset })
          }
          paginationMode="server"
          rowCount={taskList.length}
          loading={isLoading}
          slots={{
            toolbar: customToolBar,
            footer: customFooter,
          }}
        />
      </div>
    </FullScreenContainer>
  )
}

export default CreditTaskDashboardPage
